import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { DetailsPage as StepDetailsPage } from 'src/app/steps/details/details.page';
import { AutomaticStepTypes, ClientStepTypes, StepTypes } from '../StepTypes';
import * as dayjs from 'dayjs';
import { GlobalService } from 'src/app/api/global.service';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { UploadComponent } from '../../files/upload/upload.component';
import { StepTypes as stepTypeEnum } from '../StepTypes';
import { TaskStatus } from '../../tasks/TaskStatus';
import { StepStatus } from '../StepStatus';
import { QuickCommentComponent } from '../../comments/quick-add/quick-add.component';
import { EmailFilesPromptComponent } from '../email-files-prompt/email-files-prompt.component';

@Component({
  selector: 'app-step-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges {

  stepTypeEnum = stepTypeEnum;
  
  @Input() show_skeleton = false;
  @Input() create_mode = false;
  @Input() steps: any = null;
  @Input() task: any = null;
  @Input() project: any = null;
  @Input() skeleton_repeat: number = 1;
  @Input() local_only: boolean = false;
  loaded_steps = false;
  /**
   * Array of column keys to hide. Example: ['status','date'] to hide status and date columns
   */
  @Input() hideColumns: string[] = [];

  @Output() stepModified = new EventEmitter<{ data: any }>();
  today: Date;

  automaticStepTypes = AutomaticStepTypes;



  constructor(private modalController: ModalController, private alertController: AlertController, private apiService: ApiService, public globalService: GlobalService) { }

  ngOnInit() {
    console.log(this.steps);
    this.today = dayjs().toDate();
    if(!this.create_mode) this.GetProject();
    else this.loaded_steps = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.steps) {
      this.LoadChecklistItems();
      this.LoadImportantFileUploadData();
    }
  }

  // ---- Checklist actions ----
  // A task's step list doesn't carry each action's own data, so the items of any checklist actions are fetched here
  // (one small request per checklist) whenever the list is (re)loaded. Open until the action is complete, unless the
  // user has toggled it themselves.
  private checklist_expanded: { [guid: string]: boolean } = {};

  // The action's saved data ({ items: [...] }), or null if it couldn't be loaded
  private async FetchChecklistData(step: any): Promise<any | null> {
    const data: any = await this.apiService.get(`steps/${step.guid}`);
    if (!data?.success || !data.data?.[0]) return null;
    let parsed: any = null;
    if (data.data[0].steps_data?.[0]?.step_data) {
      try { parsed = JSON.parse(data.data[0].steps_data[0].step_data); } catch { parsed = null; }
    }
    const result = parsed && typeof parsed === 'object' ? parsed : {};
    if (!Array.isArray(result.items)) result.items = [];
    return result;
  }

  async LoadChecklistItems() {
    if (this.create_mode || !Array.isArray(this.steps)) return;
    const checklists = this.steps.filter((s: any) => s.type == StepTypes.checklist && s.guid);
    await Promise.all(checklists.map(async (step: any) => {
      const latest = await this.FetchChecklistData(step);
      step.data = latest ?? { items: [] };
    }));
  }

  private checklist_saving: { [guid: string]: boolean } = {};

  IsChecklistSaving(step: any): boolean {
    return !!this.checklist_saving[step.guid];
  }

  // Ticking an item straight from the list. Works from the freshly saved checklist rather than the copy loaded with the
  // table, so items someone else added or checked in the meantime aren't overwritten, and records who checked it (their
  // guid, plus their name so it can be shown without a lookup) and when.
  async ToggleChecklistItemFromTable(step: any, item: any, event: Event) {
    const box = event.target as HTMLInputElement;
    const checked = box.checked;
    this.checklist_saving[step.guid] = true;
    try {
      const latest = await this.FetchChecklistData(step);
      const target = latest?.items.find((i: any) => i.guid === item.guid);
      if (!latest || !target) {
        box.checked = !checked;
        this.globalService.presentToastMessage('This checklist has changed - it has been refreshed. Please try again.', false);
        if (latest) step.data = latest;
        return;
      }

      if (checked) {
        const user = this.globalService.GetCurrentUser();
        target.checked = true;
        target.checked_date = new Date().toISOString();
        target.checked_by_guid = user?.guid || '';
        target.checked_by = user?.display_name || '';
      } else {
        target.checked = false;
        delete target.checked_date;
        delete target.checked_by_guid;
        delete target.checked_by;
      }

      const saved: any = await this.apiService.put(`steps/${step.guid}`, { step_data: JSON.stringify(latest) });
      if (!saved?.success) {
        box.checked = !checked;
        this.globalService.presentToastMessage(saved?.message || 'Could not save the checklist', false);
        return;
      }
      step.data = latest;

      if (checked && step.status != StepStatus.complete && latest.items.length > 0 && latest.items.every((i: any) => i.checked)) {
        await this.OfferCompleteChecklist(step);
      }
    } finally {
      this.checklist_saving[step.guid] = false;
    }
  }

  private async OfferCompleteChecklist(step: any) {
    const alert = await this.alertController.create({
      header: 'Checklist Complete',
      message: `Everything on "${step.title}" is checked off. Would you like to mark this action as complete?`,
      buttons: [
        { text: 'Not Yet', role: 'cancel' },
        {
          text: 'Mark As Complete',
          handler: async () => {
            await this.globalService.presentLoading(`Completing ${step.title}`, 30000);
            await this.apiService.put(`steps/${step.guid}/complete/true`, null);
            if (this.task != null) {
              const returned_task_data: any = await this.apiService.get(`tasks/${this.task.guid}`);
              this.task = returned_task_data.data[0];
              this.stepModified.emit({ data: { task: this.task, step: step } });
            }
            await this.globalService.dismissLoading();
          }
        }
      ]
    });
    await alert.present();
  }

  // Hover text for an item that has a description. The global tooltip renders HTML, so the text is escaped first.
  GetChecklistItemTooltip(item: any): string {
    const description = (item?.description || '').trim();
    if (!description) return '';
    const escape = (t: string) => t
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .split(/\r?\n/).join('<br>');
    return `<strong>${escape(item.text || '')}</strong><br>${escape(description)}`;
  }

  GetChecklistItems(step: any): any[] {
    return step?.data?.items || [];
  }

  ChecklistCheckedCount(step: any): number {
    return this.GetChecklistItems(step).filter((i: any) => i.checked).length;
  }

  // A checklist can only be completed once every item is checked (and only once its items have loaded)
  ChecklistAllChecked(step: any): boolean {
    return !!step?.data?.items && this.ChecklistCheckedCount(step) === step.data.items.length;
  }

  ChecklistPercent(step: any): number {
    const total = this.GetChecklistItems(step).length;
    return total === 0 ? 0 : Math.round((this.ChecklistCheckedCount(step) / total) * 100);
  }

  IsChecklistExpanded(step: any): boolean {
    const chosen = this.checklist_expanded[step.guid];
    return chosen !== undefined ? chosen : step.status != StepStatus.complete;
  }

  ToggleChecklist(step: any) {
    this.checklist_expanded[step.guid] = !this.IsChecklistExpanded(step);
  }

  // ---- Upload File actions ----
  // Same idea as checklist items above (see LoadChecklistItems/FetchChecklistData) - a task's step
  // list doesn't carry each action's own data, so it's fetched here whenever the list is (re)loaded.
  // Needed even in "general dropbox" mode (no specific files requested), since request_specific_files
  // itself lives on this same fetched data. Covers both upload_file (the only one still creatable)
  // and important_file_upload (the legacy type it superseded - still needs this for any step that
  // already exists as one).
  IsUploadFileStep(step: any): boolean {
    return step?.type == StepTypes.upload_file || step?.type == StepTypes.important_file_upload;
  }

  async LoadImportantFileUploadData() {
    if (this.create_mode || !Array.isArray(this.steps)) return;
    const uploads = this.steps.filter((s: any) => this.IsUploadFileStep(s) && s.guid);
    await Promise.all(uploads.map(async (step: any) => {
      const latest = await this.FetchChecklistData(step);
      step.data = latest ?? {};
    }));
  }

  GetSpecificFileRequests(step: any): any[] {
    return step?.data?.specific_files || [];
  }

  SpecificFilesUploadedCount(step: any): number {
    return this.GetSpecificFileRequests(step).filter((f: any) => f.uploaded).length;
  }

  // Can only offer completion once every requested file has been uploaded (and only once loaded).
  SpecificFilesAllUploaded(step: any): boolean {
    const items = this.GetSpecificFileRequests(step);
    return items.length > 0 && this.SpecificFilesUploadedCount(step) === items.length;
  }

  SpecificFilesPercent(step: any): number {
    const total = this.GetSpecificFileRequests(step).length;
    return total === 0 ? 0 : Math.round((this.SpecificFilesUploadedCount(step) / total) * 100);
  }

  private specific_files_expanded: { [guid: string]: boolean } = {};

  IsSpecificFilesExpanded(step: any): boolean {
    const chosen = this.specific_files_expanded[step.guid];
    return chosen !== undefined ? chosen : step.status != StepStatus.complete;
  }

  ToggleSpecificFiles(step: any) {
    this.specific_files_expanded[step.guid] = !this.IsSpecificFilesExpanded(step);
  }

  private specific_file_saving: { [key: string]: boolean } = {};

  IsSpecificFileSaving(step: any, file: any): boolean {
    return !!this.specific_file_saving[step.guid + '_' + file.name];
  }

  // Upload against one specific requested item (as opposed to presentStepFileUpload's generic
  // dropbox, which fulfills the whole step at once). Files a team member drops in here get linked
  // to the step exactly like any other upload (see AddFileToStep/presentStepFileUpload) - Steps.cs
  // marks them important/visible-to-client server-side since this step is important_file_upload.
  async presentSpecificFileUpload(step: any, file: any) {
    const modal = await this.modalController.create({
      component: UploadComponent,
      cssClass: 'small-modal',
      componentProps: {
        step: step,
        folder_guid: this.task.guid,
        title: `Upload "${file.name}"`
      },
    });
    modal.onDidDismiss().then(async (data: any) => {
      if (data.data && data.data.length > 0) await this.FulfillSpecificFile(step, file, data.data);
    });
    return await modal.present();
  }

  async FulfillSpecificFile(step: any, file: any, uploaded_files: any[]) {
    const key = step.guid + '_' + file.name;
    this.specific_file_saving[key] = true;
    try {
      // Works from the freshly saved data (same reasoning as ToggleChecklistItemFromTable) so a
      // teammate uploading a different requested item at the same time isn't overwritten.
      const latest = await this.FetchChecklistData(step);
      const target = (latest?.specific_files || []).find((f: any) => f.name === file.name);
      if (!latest || !target) {
        this.globalService.presentToastMessage('This action has changed - it has been refreshed. Please try again.', false);
        if (latest) step.data = latest;
        return;
      }

      for (const uploaded of uploaded_files) {
        await this.apiService.post(`steps/${step.guid}/files/${uploaded.guid}`, null);
      }
      // Only one upload slot per requested item - if several files were dropped in at once, the
      // last one wins as "the" file for this item (all of them are still linked to the step above).
      const last = uploaded_files[uploaded_files.length - 1];
      target.uploaded = true;
      target.file_guid = last.guid;
      target.file_name = last.file_name;

      const saved: any = await this.apiService.put(`steps/${step.guid}`, { step_data: JSON.stringify(latest) });
      if (!saved?.success) {
        this.globalService.presentToastMessage(saved?.message || 'Could not save this upload', false);
        return;
      }
      step.data = latest;

      if (step.status != StepStatus.complete && latest.specific_files.length > 0 && latest.specific_files.every((f: any) => f.uploaded)) {
        await this.OfferCompleteSpecificFiles(step);
      }
    } finally {
      this.specific_file_saving[key] = false;
    }
  }

  private async OfferCompleteSpecificFiles(step: any) {
    const alert = await this.alertController.create({
      header: 'All Files Uploaded',
      message: `Every file requested for "${step.title}" has been uploaded. Would you like to mark this action as complete?`,
      buttons: [
        { text: 'Not Yet', role: 'cancel' },
        {
          text: 'Mark As Complete',
          handler: async () => {
            await this.globalService.presentLoading(`Completing ${step.title}`, 30000);
            await this.apiService.put(`steps/${step.guid}/complete/true`, null);
            if (this.task != null) {
              const returned_task_data: any = await this.apiService.get(`tasks/${this.task.guid}`);
              this.task = returned_task_data.data[0];
              this.stepModified.emit({ data: { task: this.task, step: step } });
            }
            await this.globalService.dismissLoading();
          }
        }
      ]
    });
    await alert.present();
  }

  async GetProject() {
    if(this.project == null) {
      let project_data: any = await this.apiService.get(`projects/${this.task.project_guid}`);
      if(project_data.success) {
        this.project = project_data.data[0];
      }
    }
    this.loaded_steps = true;
  }

  // True when the task itself is paused, or its whole project is paused (which cascades paused=1
  // down to every task/step server-side, but the already-loaded project.tasks[] in memory can lag
  // behind that until refetched - checking project.paused here closes that gap immediately).
  IsTaskPaused(): boolean {
    return !!(this.task?.paused || this.project?.paused);
  }

  IsPreviousTaskCompleted() {
    if (!this.project || !this.project.tasks || this.project.tasks.length === 0) {
      return true;
    }
    if(!this.project.force_order) {
      return true;
    } 

    const currentTaskIndex = this.project.tasks.findIndex(t => t.guid === this.task.guid);
    if (currentTaskIndex <= 0) {
      return true;
    }

    const previousTask = this.project.tasks[currentTaskIndex - 1];
    if(previousTask.status == TaskStatus.awaiting_previous_task) return false;

    if(previousTask.status != TaskStatus.completed) {
      return false;
    }
    else return true;
  }

  GetSkippedActionDescription(stepType: StepTypes): string {
    switch (stepType) {
      case StepTypes.email:
        return 'sending this email';
      case StepTypes.approval_request:
        return 'sending this approval request';
      case StepTypes.document_request:
        return 'sending this document request';
      case StepTypes.payment_request:
        return 'sending this payment request';
      case StepTypes.signature_request:
        return 'sending this signature request';
      case StepTypes.survey:
        return 'sending this survey';
      case StepTypes.webhook:
        return 'triggering this webhook';
      case StepTypes.activate_client_portal:
        return 'activating this client portal';
      case StepTypes.appointment_request:
        return 'sending this appointment request';
      case StepTypes.question_request:
        return 'sending this question request';
      case StepTypes.checklist_request:
        return 'sending this checklist request';
      default:
        return 'executing this action';
    }
  }

  async ForceCompleteStep(step: any) {
    console.log("Trying to force complete step", step);
    if(step.status == StepStatus.complete) return;
    
    const isStart = step.type == StepTypes.email || step.type == StepTypes.approval_request || step.type == StepTypes.document_request || step.type == StepTypes.payment_request || step.type == StepTypes.signature_request || step.type == StepTypes.survey || step.type == StepTypes.webhook || step.type == StepTypes.activate_client_portal || step.type == StepTypes.appointment_request || step.type == StepTypes.question_request || step.type == StepTypes.checklist_request;
    const shouldOfferStartInstead = isStart && step.status != StepStatus.awaiting_client;
    const skippedActionDescription = this.GetSkippedActionDescription(step.type);
    
    let message = `Are you sure you want to force complete "${step.title}"?`;
    if (shouldOfferStartInstead) {
      message += ` By doing this you will skip ${skippedActionDescription} and any logic associated with this action.`;
      message += `\n\n<b>Note: Force completing will skip ${skippedActionDescription}. If you want the action logic to execute, select "Start Action Instead".</b>`;
    }

    const buttons: any[] = [
      { text: 'Cancel', role: 'cancel' },
      {
        text: 'Force Complete',
        handler: async () => {
          await this.globalService.presentLoading(`Forcing Complete ${step.title}`, 30000);
          await this.apiService.put(`steps/${step.guid}/complete/true`, null);
          await this.globalService.dismissLoading();
          if (this.task != null) {
            let returned_task_data: any = await this.apiService.get(`tasks/${this.task.guid}`);
            this.task = returned_task_data.data[0];
            this.stepModified.emit({ data: { task: this.task, step: step } });
          }
        }
      }
    ];

    if (shouldOfferStartInstead) {
      buttons.splice(1, 0, {
        text: 'Start Action Instead',
        handler: () => {
          void this.CompleteStep(step);
        }
      });
    }
    
    const alert = await this.alertController.create({
      header: 'Force Complete Action?',
      message: message,
      buttons: buttons
    });
    await alert.present();
  }

  async StartConditionalStep(step) {
    const options: any[] = step.data?.options || [];
    if (options.length === 0) {
      this.globalService.presentToastMessage('This conditional action has no options configured.', false);
      return;
    }

    const optionButtons = options.map(option => ({
      text: option.label || 'Unnamed Option',
      handler: async () => {
        await this.globalService.presentLoading(`Starting: ${option.label}`, 30000);
        await this.apiService.put(`steps/${step.guid}/conditional/${option.step_guid || 'none'}`, null);
        await this.globalService.dismissLoading();
        if (this.task != null) {
          let returned_task_data: any = await this.apiService.get(`tasks/${this.task.guid}`);
          this.task = returned_task_data.data[0];
          this.stepModified.emit({ data: { task: this.task, step: step } });
        }
      }
    }));

    const alert = await this.alertController.create({
      header: step.title,
      message: 'Select an option to proceed. The linked action will start and all other linked actions will be skipped.',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        ...optionButtons
      ]
    });
    await alert.present();
  }

  // Reads review_before_sending from the saved step data rather than step.data, which can be stale in the table.
  // Applies to every client action that sends an email (ClientStepTypes) - a signature request is already
  // always reviewed through its own setup before it is sent.
  private async IsReviewBeforeSending(step: any): Promise<boolean> {
    if (!ClientStepTypes.includes(Number(step.type))) return false;
    const step_data: any = await this.apiService.get(`steps/${step.guid}`);
    const raw = step_data?.success ? step_data.data?.[0]?.steps_data?.[0]?.step_data : null;
    try { return !!(raw && JSON.parse(raw)?.review_before_sending); } catch { return false; }
  }

  async CompleteStep(step) {
    // "Review before sending" actions are started from the action details page, which shows the email for
    // review (edit / attach files) first and only sends it once the user clicks Send Email.
    const review_before_sending = await this.IsReviewBeforeSending(step);
    if (review_before_sending) {
      return this.presentStepDetails(step, null, true);
    }
    const isStart = step.type == StepTypes.email || step.type == StepTypes.approval_request || step.type == StepTypes.document_request || step.type == StepTypes.payment_request || step.type == StepTypes.signature_request || step.type == StepTypes.survey || step.type == StepTypes.webhook || step.type == StepTypes.activate_client_portal || step.type == StepTypes.appointment_request || step.type == StepTypes.question_request || step.type == StepTypes.checklist_request;
    const action = isStart ? 'Start' : 'Complete';
    const alert = await this.alertController.create({
      header: `${action} Action`,
      message: `Are you sure you want to ${action.toLowerCase()} "${step.title}"?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading(`${action}ing ${step.title}`, 30000);
            if(step.type == StepTypes.checkbox || step.type == StepTypes.upload_file || step.type == StepTypes.important_file_upload || step.type == StepTypes.standard_operating_procedure || step.type == StepTypes.checklist) {
              await this.apiService.put(`steps/${step.guid}/complete/true`, null);
            }
            else if (isStart) {
              await this.apiService.put(`steps/${step.guid}/start/true`, null);
            }

            if (this.task != null) {
              let returned_task_data: any = await this.apiService.get(`tasks/${this.task.guid}`);
              this.task = returned_task_data.data[0];
              let return_data = {
                task: this.task,
                step: step
              }
              this.stepModified.emit({ data: return_data });
            }

            this.globalService.dismissLoading();

            // Every "start" action except a signature request creates a secure email behind the
            // scenes (ClientStepTypes) - offer to attach more files right after, now that the email
            // (and anything "add task files to email" already put on it) actually exists to attach to.
            // Not for "Review before sending" actions: files are added during the review, before it's sent.
            if (isStart && ClientStepTypes.includes(step.type) && !review_before_sending) {
              await this.PromptForMoreEmailFiles(step);
            }
          }
        }
      ]
    });
    await alert.present();
  }

  private async PromptForMoreEmailFiles(step: any) {
    const step_data: any = await this.apiService.get(`steps/${step.guid}`);
    const email_guid = step_data?.success ? step_data.data?.[0]?.steps_data?.[0]?.email_guid : null;
    if (!email_guid) return;

    const modal = await this.modalController.create({
      component: EmailFilesPromptComponent,
      cssClass: 'small-modal secondary-modal',
      componentProps: { email_guid, step_title: step.title }
    });
    await modal.present();
  }

  async AddFileToStep(step,files) {
    await this.globalService.presentLoading(`Adding File to ${step.title}`, 30000);
    for(let file of files) {
      await this.apiService.post(`steps/${step.guid}/files/${file.guid}`, null);
    }
    this.globalService.dismissLoading();
  }

  async presentStepDetails(step,previousStep, review_on_open = false) {
    console.log(step);
    let prev_guid = null;
    if(previousStep) prev_guid = previousStep.guid;
    console.log(previousStep);
    const modal = await this.modalController.create({
      component: StepDetailsPage,
      cssClass: 'large-modal',
      componentProps: {
        guid: step.guid,
        previous_step_guid: prev_guid,
        review_on_open: review_on_open
      },
    })
     modal.onDidDismiss().then(async (data: any) => {
      let returned_task_data: any = await this.apiService.get(`tasks/${this.task.guid}`);
      this.task = returned_task_data.data[0];
      // Checklist items may have been ticked off in the details modal
      await this.LoadChecklistItems();
      let return_data = {
        task: this.task,
        step: step
      }
      this.stepModified.emit({ data: return_data });
    });


    return await modal.present()
  }

  GetCommentTooltip(item: any): string {
    if (!item.step_last_comment_date) return 'No comments yet — click to add';
    return `Last comment: ${dayjs(this.globalService.DateToUTCFormat(item.step_last_comment_date)).format('MMM D, YYYY h:mm A')}`;
  }

  async OpenAddComment(item: any) {
    const modal = await this.modalController.create({
      component: QuickCommentComponent,
      cssClass: 'small-modal',
      componentProps: { url: 'steps/comments', guid: item.guid, title: 'Add Comment to Step' }
    });
    return await modal.present();
  }

  async OpenQuickComment(item: any) {
    const modal = await this.modalController.create({
      component: StepDetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: item.guid, previous_step_guid: null, activeIndex: 'Comments' }
    });
    modal.onDidDismiss().then(async () => {
      const returned_task_data: any = await this.apiService.get(`tasks/${this.task.guid}`);
      this.task = returned_task_data.data[0];
      this.stepModified.emit({ data: { task: this.task } });
    });
    return await modal.present();
  }

  async presentStepFileUpload(step) {
    console.log(step);
    const modal = await this.modalController.create({
      component: UploadComponent,
      cssClass: 'small-modal',
      componentProps: {
        step: step,
        folder_guid: this.task.guid,
        title: `Upload Files to: ${step.title}`
      },
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        console.log("Complete Step");
        this.AddFileToStep(step,data.data);
        this.CompleteStep(step);
      }
      else{
        console.log("No Files Uploaded");
      }
    });
    return await modal.present()
  }

}
